import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Sparkles } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";

const WHEEL = "/gallery/img_7035.webp";
const GLASS = "/gallery/img_7034.webp";
const RIM = "/gallery/img_7035.webp";
const POLISH = "/gallery/img_7034.webp";
const CERAMIC = "/gallery/img_7051.webp";
const CORRECTION = "/gallery/img_7279.webp";

const PACKAGES = [
  {
    id: "express",
    name: "Express Detail",
    price: "$75 + tax",
    duration: "90 minutes",
    blurb: "A fast, thorough refresh that keeps your car healthy between deep details.",
    steps: [
      { step: "Hand wash & dry", img: "/gallery/img_6291.webp", why: "Every panel is washed by hand and dried with soft microfibre — no automatic brushes that drag grit across your paint and leave swirl marks." },
      { step: "Wheels & tires cleaned", img: WHEEL, why: "Brake dust is acidic and slowly pits your rims. Removing it protects the finish, and dressing the tires restores that deep, factory-black look." },
      { step: "Interior vacuum", img: "/gallery/img_6778.webp", why: "Sand and grit ground into carpet and seats act like sandpaper, wearing fibres down every time you sit. Pulling it out extends the life of your interior." },
      { step: "Windows in & out", img: GLASS, why: "Clean glass isn't just about looks — it removes the hazy film that scatters headlight glare and strains your eyes when driving at night." },
      { step: "Dash & console wipe-down", img: "/gallery/img_7053.webp", why: "Dust and UV bake into plastics and cause fading and cracking. Wiping them down lifts grime before it sets into the surface." },
    ],
  },
  {
    id: "full",
    name: "Full Detail",
    price: "from $169 + tax",
    duration: "3–4 hours",
    popular: true,
    blurb: "A true deep clean, inside and out — where a tired car is brought back to life.",
    steps: [
      { step: "Everything in the Express Detail", img: "/gallery/img_6329.webp", why: "We start with the full exterior wash, wheels, vacuum and glass — then go far deeper from there." },
      { step: "Deep interior shampoo & extraction", img: "/gallery/img_6753.webp", why: "Hot-water extraction lifts out embedded dirt, salt stains and trapped odours that a surface vacuum simply leaves behind." },
      { step: "Leather clean & condition", img: "/gallery/img_7038.webp", why: "Conditioning replaces the natural oils that keep leather soft, preventing the drying, cracking and hardening that quietly destroy resale value." },
      { step: "Clay bar treatment", img: "/gallery/img_7051.webp", why: "Clay pulls bonded contaminants — rail dust, industrial fallout, tree sap — out of the paint. You can feel them but can't wash them off, and the result is glass-smooth paint." },
      { step: "Hand wax protection", img: "/gallery/img_7279.webp", why: "A layer of wax is a sacrificial shield against UV, road salt and water spotting — and it makes your car's colour visibly deeper and richer." },
      { step: "Streak-free glass, all around", img: GLASS, why: "Properly finished glass eliminates the smears that catch the sun and reduce visibility, inside and out." },
    ],
  },
  {
    id: "ceramic",
    name: "Ceramic Coating",
    price: "from $249 + tax",
    duration: "1–2 days",
    blurb: "Multi-year, showroom-grade protection that changes how your car lives outdoors.",
    steps: [
      { step: "Full paint correction", img: POLISH, why: "We machine-polish away years of swirl marks and light scratches first — so the coating locks in flawless paint instead of sealing defects in permanently." },
      { step: "9H ceramic coating", img: CORRECTION, why: "A semi-permanent glass layer chemically bonds to your clear coat. It's far harder than wax and shrugs off light scratches, bird droppings and harsh chemicals." },
      { step: "Up to 5-year protection", img: "/gallery/img_7279.webp", why: "One application carries your car through years of brutal Ottawa winters — no more re-waxing every few months to stay protected." },
      { step: "Hydrophobic finish", img: CERAMIC, why: "Water beads up and sheets off, dragging dirt with it. Your car stays cleaner far longer and rinses clean with a fraction of the effort." },
      { step: "Gloss enhancement", img: "/gallery/img_7051.webp", why: "The coating adds depth and a wet, liquid-glass shine that traditional wax physically cannot achieve." },
      { step: "Wheel face coating", img: RIM, why: "Coated wheels resist baked-on brake dust and rinse clean, protecting the finish from constant heat and grime." },
    ],
  },
];

export default function PackageDetails() {
  const [active, setActive] = useState("full");
  const pkg = PACKAGES.find((p) => p.id === active);

  return (
    <section data-testid="package-details" className="bg-brand-offwhite">
      <div className="container-rv py-24 lg:py-32">
        <Reveal className="mb-12 max-w-2xl">
          <p className="overline text-brand-red">What you're actually paying for</p>
          <h2 className="mt-4 font-serif text-4xl font-bold leading-tight text-brand-charcoal sm:text-5xl">
            Every step, and why it matters for your car
          </h2>
          <p className="mt-5 text-brand-muted">
            Detailing isn't just "a wash." Each stage protects your paint, your interior and your car's value.
            Here's exactly what's included in each package — and the reason behind every step.
          </p>
        </Reveal>

        {/* Tabs */}
        <div className="mb-10 flex flex-wrap gap-3">
          {PACKAGES.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              data-testid={`package-tab-${p.id}`}
              className={`group relative rounded-2xl border px-6 py-4 text-left transition-all duration-300 ${
                active === p.id
                  ? "border-brand-red bg-white shadow-[0_24px_60px_-40px_rgba(200,16,46,0.5)]"
                  : "border-brand-silver/50 bg-white/50 hover:border-brand-charcoal/30"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className={`font-serif text-lg font-bold ${active === p.id ? "text-brand-red" : "text-brand-charcoal"}`}>
                  {p.name}
                </span>
                {p.popular && (
                  <span className="rounded-full bg-brand-red px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-white">
                    Most Popular
                  </span>
                )}
              </span>
              <span className="mt-1 flex items-center gap-3 text-xs text-brand-muted">
                <span className="font-semibold text-brand-charcoal">{p.price}</span>
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.duration}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Active package explanation */}
        <div key={pkg.id} data-testid={`package-panel-${pkg.id}`} className="animate-in fade-in duration-500">
          <div className="mb-8 flex items-start gap-3">
            <Sparkles className="mt-1 h-5 w-5 shrink-0 text-brand-red" />
            <p className="max-w-2xl text-lg text-brand-charcoal/80">{pkg.blurb}</p>
          </div>

          <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
            {pkg.steps.map((s, i) => (
              <Reveal key={s.step} delay={(i % 2) * 0.08}>
                <div className="flex gap-4 rounded-2xl border border-brand-silver/40 bg-white p-4 transition-shadow duration-300 hover:shadow-[0_24px_60px_-45px_rgba(26,28,30,0.5)]">
                  <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl sm:h-28 sm:w-28">
                    <img
                      src={s.img}
                      alt={`${s.step} — Reverence Autos detailing`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-brand-red font-serif text-[0.7rem] font-bold text-white">
                      {i + 1}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-serif text-lg font-bold leading-tight text-brand-charcoal">{s.step}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-brand-muted">{s.why}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-5 rounded-2xl bg-brand-charcoal p-8 text-white">
            <div className="flex-1">
              <p className="font-serif text-2xl font-bold">Ready for the {pkg.name}?</p>
              <p className="mt-1 text-sm text-brand-silver">We come to your driveway — pick a date and we'll handle the rest.</p>
            </div>
            <Link to={`/book?service=${pkg.id}`} data-testid={`package-book-${pkg.id}`} className="btn-red">
              Book the {pkg.name.split(" ")[0]} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
